import React, { createContext, useContext, useState, useEffect } from "react";
import { createSession, getSessionData } from "../api";
import apiClient from "@lift-admin/utils/apiClient"; // Import apiClient
import { useGenerationWarning } from "../hooks/useGenerationWarning";

// Add a shared event for new chat
export const NEW_CHAT_EVENT = "newChatEvent";

// Type for brand map
export type BrandMap = {
  [brandId: string]: string;
};

interface ChatV2ContextProps {
  open: boolean;
  toggleOpen: () => void;
  startNewChat: () => void;
  activeSessionId: string | null;
  setActiveSessionId: (sessionId: string) => void;
  createNewSession: () => Promise<string>;
  isFromHistory: boolean;
  setIsFromHistory: (value: boolean) => void;
  agencyMode: boolean;
  setAgencyMode: (value: boolean) => void;
  brandMap: BrandMap;
  setBrandMap: (brandMap: BrandMap) => void;
  model: string;
  setModel: (value: string) => void;
  users: any[]; // Add users to props
  fetchUsers: () => Promise<void>; // Add fetchUsers to props
  userMap: Record<string, string>;
  fetchBrands: () => Promise<void>;
  // HTML View management
  showHtmlView: boolean;
  htmlContent: string;
  pendingHtmlContent: string | null;
  handleViewHtml: (html: string) => void;
  closeHtmlView: () => void;
  // Generation warning functionality
  isGenerating: boolean;
  setIsGenerating: (generating: boolean) => void;
  stopGeneration: (() => void) | null;
  setStopGeneration: (stopFn: (() => void) | null) => void;
  checkGenerationAndExecute: (
    action: "close" | "newChat" | "switchSession",
    callback: () => void
  ) => void;
  generationWarning: {
    isDialogOpen: boolean;
    setIsDialogOpen: (open: boolean) => void;
    pendingAction: {
      action: "close" | "newChat" | "switchSession";
      callback: () => void;
    } | null;
    handleConfirm: () => void;
    handleCancel: () => void;
  };
}

const ChatV2Context = createContext<ChatV2ContextProps | undefined>(undefined);

export const ChatV2Provider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [open, setOpen] = useState(false);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [isFromHistory, setIsFromHistory] = useState(false);
  const [savedSessionId, setSavedSessionId] = useState<string | null>(null);
  const [agencyMode, setAgencyMode] = useState<boolean>(false);
  const [brandMap, setBrandMap] = useState<BrandMap>({});
  const [userMap, setUserMap] = useState<Record<string, string>>({});
  const [model, setModel] = useState<string>("claude");
  const [users, setUsers] = useState<any[]>([]); // New users state

  // HTML View state
  const [showHtmlView, setShowHtmlView] = useState(false);
  const [htmlContent, setHtmlContent] = useState<string>("");
  const [pendingHtmlContent, setPendingHtmlContent] = useState<string | null>(
    null
  );

  // Generation warning state
  const [isGenerating, setIsGenerating] = useState(false);
  const [stopGeneration, setStopGeneration] = useState<(() => void) | null>(
    null
  );
  const generationWarning = useGenerationWarning();

  const toggleOpen = () => {
    // When closing the chat, save the current session ID
    if (open && activeSessionId) {
      setSavedSessionId(activeSessionId);
    }

    // When reopening, restore the saved session if available
    if (!open && savedSessionId) {
      setActiveSessionId(savedSessionId);
    }

    setOpen((prev) => !prev);
  };

  // Fetch users from the API (app users + Slack users merged)
  const fetchUsers = async () => {
    try {
      const response = await apiClient.get(`/user/v1/admin/all`);
      setUsers(response.data.response);

      // Build user map { user_id: user_name } from app users
      const uMap: Record<string, string> = {};
      response.data.response.forEach((u: any) => {
        uMap[String(u.user_id ?? u.id)] =
          u.name ?? u.full_name ?? u.email ?? String(u.user_id ?? u.id);
      });

      // Also fetch Slack users and merge into the same map
      try {
        const { API_ENDPOINTS_V2 } = await import("../lib/apiConfig");
        const slackRes = await apiClient.get(API_ENDPOINTS_V2.SLACK_USER_MAP);
        if (slackRes.data?.success && slackRes.data?.data) {
          const slackMap = slackRes.data.data as Record<string, string>;
          Object.entries(slackMap).forEach(([slackId, displayName]) => {
            if (slackId && displayName) {
              uMap[slackId] = displayName;
            }
          });
        }
      } catch (slackErr) {
        console.warn("Failed to fetch Slack user map (non-critical):", slackErr);
      }

      setUserMap(uMap);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  // Fetch brands from the API and populate brandMap
  const fetchBrands = async () => {
    try {
      const res = await apiClient.get(`/brand/v1/user/brand/all`);
      const bMap: BrandMap = {};
      const data = res.data.response ?? [];
      data.forEach((b: any) => {
        bMap[String(b.brand_id ?? b.id)] =
          b.name ?? b.brand_name ?? `Brand ${b.brand_id ?? b.id}`;
      });
      setBrandMap(bMap);
    } catch (err) {
      console.error("Error fetching brands:", err);
    }
  };

  useEffect(() => {
    fetchUsers(); // Fetch users when the context is initialized
    fetchBrands();
  }, []);

  // ------------------------------------------------------------------
  // URL PARAMS  ➜  SESSION HANDLING (behaviour parity with Chatbot-V1)
  // ------------------------------------------------------------------
  // 1)  On first load, inspect URL for both session & view, validate session,
  //     open the chat, then migrate any legacy search params into the hash
  //     preserving both session and view values.
  useEffect(() => {
    if (typeof window === "undefined") return;

    async function handleInitialParams() {
      const hashParts = window.location.hash.split("?");
      const hashParams = new URLSearchParams(hashParts[1] || "");
      const searchParams = new URLSearchParams(window.location.search);

      const sessionId =
        hashParams.get("session") || searchParams.get("session");
      const viewParam = hashParams.get("view") || searchParams.get("view");

      if (sessionId) {
        try {
          const sessionData = await getSessionData(sessionId);
          const metadata = sessionData.metadata || {};
          if (metadata.model) {
            setModel(metadata.model);
          }
          setActiveSessionId(sessionId);
          setOpen(true);
        } catch (err) {
          console.error(`Invalid sessionId in URL: ${sessionId}`, err);
        }
      }

      if (sessionId || viewParam) {
        const newSearch = new URLSearchParams(window.location.search);
        newSearch.delete("session");
        newSearch.delete("view");

        const newHashParams = new URLSearchParams();
        if (sessionId) newHashParams.set("session", sessionId);
        if (viewParam) newHashParams.set("view", viewParam);

        const url = new URL(window.location.href);
        url.search = newSearch.toString();
        url.hash = `${hashParts[0] || "#"}?${newHashParams.toString()}`;
        window.history.replaceState({}, "", url.toString());
      }
    }

    handleInitialParams();
  }, []);

  // 2)  Keep the URL hash parameter 'session' in sync whenever the
  //     activeSessionId changes inside the chat.
  useEffect(() => {
    if (typeof window === "undefined") return;

    const url = new URL(window.location.href);
    const hashParts = url.hash.split("?");
    const hashPath = hashParts[0] || "#";
    const hashParams = new URLSearchParams(hashParts[1] || "");

    if (activeSessionId) {
      hashParams.set("session", activeSessionId);
    } else {
      hashParams.delete("session");
    }

    url.hash =
      hashPath + (hashParams.toString() ? "?" + hashParams.toString() : "");
    window.history.pushState({}, "", url.toString());
  }, [activeSessionId]);

  // 3)  When the whole chat widget closes, strip session + view params
  //     and reset the activeSessionId for a clean slate.
  useEffect(() => {
    if (typeof window === "undefined") return;

    if (!open) {
      const url = new URL(window.location.href);
      const hashPath = url.hash.split("?")[0]; // preserve any hash path
      window.history.pushState(
        {},
        "",
        `${url.origin}${url.pathname}${hashPath}`
      );
      setActiveSessionId(null);
    }
  }, [open]);
  // ------------------------------------------------------------------
  // END URL PARAMS HANDLING
  // ------------------------------------------------------------------

  // When a session is loaded (activeSessionId changes), fetch its metadata and set the model
  useEffect(() => {
    if (!activeSessionId) return;
    async function loadModelMetadata() {
      try {
        const sessionData = await getSessionData(activeSessionId as string);
        const metadata = sessionData.metadata || {};
        if (metadata.model) {
          setModel(metadata.model);
        }
      } catch (err) {
        console.error(
          `Failed to load model for session ${activeSessionId}:`,
          err
        );
      }
    }
    loadModelMetadata();
  }, [activeSessionId]);

  console.log(agencyMode, "check agency");

  const startNewChat = () => {
    // Reset the active session ID
    setActiveSessionId(null);

    // Close HTML view when starting a new chat
    closeHtmlView();

    // Create a custom event to trigger new chat
    const newChatEvent = new CustomEvent(NEW_CHAT_EVENT);
    document.dispatchEvent(newChatEvent);
  };

  // Function to check generation status and show warning if needed
  const checkGenerationAndExecute = (
    action: "close" | "newChat" | "switchSession",
    callback: () => void
  ) => {
    generationWarning.checkGenerationAndExecute(isGenerating, action, () => {
      // When generation is stopped, call the stop function if available
      if (stopGeneration) {
        stopGeneration();
      }
      // Reset the generating state
      setIsGenerating(false);
      callback();
    });
  };

  // Create a new session and set it as active
  const createNewSession = async (): Promise<string> => {
    try {
      // Include brandMap, agencyMode, and model in the initial session metadata
      const metadata = {
        brandMap,
        agencyMode,
        model,
      };

      console.log(
        `[ChatV2Context] Creating session with initial metadata:`,
        metadata
      );
      const session = await createSession(undefined, metadata);

      setActiveSessionId(session.session_id);
      setSavedSessionId(session.session_id); // Also save as the last active session
      return session.session_id;
    } catch (error) {
      console.error("Failed to create session:", error);
      throw error;
    }
  };

  // Handle viewing HTML content
  const handleViewHtml = (html: string) => {
    // Only set htmlContent directly if we're already in fullscreen mode
    // Otherwise, store it as pendingHtmlContent to be used when fullscreen is entered
    if (open) {
      setHtmlContent(html);
      setShowHtmlView(true);
      setPendingHtmlContent(null);
    } else {
      // Store as pending content - will be applied when fullscreen is entered
      setPendingHtmlContent(html);
      // Don't set showHtmlView here - wait until fullscreen
    }
  };

  // Close HTML view
  const closeHtmlView = () => {
    setHtmlContent("");
    setShowHtmlView(false);
    setPendingHtmlContent(null);

    // Dispatch event to notify components that the HTML view has been closed
    document.dispatchEvent(new CustomEvent("htmlVisualizationClosed"));
  };

  // When we enter fullscreen and have pending content, apply it
  useEffect(() => {
    if (open && pendingHtmlContent) {
      setHtmlContent(pendingHtmlContent);
      setShowHtmlView(true);
      setPendingHtmlContent(null);
    }
  }, [open, pendingHtmlContent]);

  // Automatically close any open HTML view when the active session changes
  useEffect(() => {
    closeHtmlView();
  }, [activeSessionId]);

  return (
    <ChatV2Context.Provider
      value={{
        open,
        toggleOpen,
        startNewChat,
        activeSessionId,
        setActiveSessionId,
        createNewSession,
        isFromHistory,
        setIsFromHistory,
        agencyMode,
        setAgencyMode,
        brandMap,
        setBrandMap,
        model,
        setModel,
        users, // Expose users
        fetchUsers, // Expose fetchUsers
        userMap,
        fetchBrands,
        // HTML View management
        showHtmlView,
        htmlContent,
        pendingHtmlContent,
        handleViewHtml,
        closeHtmlView,
        // Generation warning functionality
        isGenerating,
        setIsGenerating,
        stopGeneration,
        setStopGeneration,
        checkGenerationAndExecute,
        generationWarning,
      }}
    >
      {children}
    </ChatV2Context.Provider>
  );
};

export const useChatV2 = (): ChatV2ContextProps => {
  const context = useContext(ChatV2Context);
  if (!context) {
    throw new Error("useChatV2 must be used within a ChatV2Provider");
  }
  return context;
};
